import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { BookingWizard } from "../../../types";
import { useGetRequiredWizardSteps$key } from "./__generated__/useGetRequiredWizardSteps.graphql";

export const useGetRequiredWizardSteps = ({
  sessionRef,
  groupSize,
  requirePlayerCounts,
  isDurationFacility,
  availableCapacity,
  isLesson,
  isRequest,
  isPrivate,
  isGolf,
  canSelectVenue,
  openSlots,
  requestGroupCount,
}: {
  sessionRef: useGetRequiredWizardSteps$key | null;
  groupSize: number;
  requirePlayerCounts: boolean;
  isDurationFacility: boolean;
  availableCapacity?: number;
  isLesson?: boolean;
  isRequest?: boolean;
  isPrivate?: boolean;
  isGolf?: boolean;
  canSelectVenue?: boolean;
  openSlots?: number;
  requestGroupCount?: number;
}) => {
  const data = useFragment(useGetRequiredWizardStepsFragmentString, sessionRef);

  const hasPlayedwithOtherPatrons =
    data?.member?.club?.clients?.edges?.length &&
    data?.member?.club?.clients?.edges?.length > 0;

  const hasPreviousBookings =
    data?.member?.repeatableBookings?.length &&
    data?.member?.repeatableBookings?.some((booking) => {
      const playerIds = booking?.rounds?.edges
        ?.map((r) => r?.node?.player?.id)
        .filter((id) => !!id);
      return (playerIds?.length ?? 0) > 1;
    });

  const requiredWizardSteps: BookingWizard.BookingWizardStepTitle[] =
    useMemo(() => {
      // if availableCapacity is 1 and isLesson and canSelectVenue is false, then we don't need to show the wizard steps, because the booking will be auto-completed
      const isAutoCompleteLesson =
        isLesson && (availableCapacity ?? 0) === 1 && !canSelectVenue;
      if (isAutoCompleteLesson) {
        return [];
      }

      // if there is private golf booking, then we don't need to show the booking details step
      const steps: BookingWizard.BookingWizardStepTitle[] =
        isPrivate && isGolf ? [] : ["BookingDetails"];

      if (
        hasPreviousBookings &&
        !isLesson &&
        !isRequest &&
        (openSlots ?? 0) > 1
      ) {
        steps.unshift("PreviousBookings");
      }

      const requiresMorePlayers =
        groupSize > 1 || (requirePlayerCounts && isDurationFacility);
      const hasPlayedWithOthersAndRequiresPlayers = Boolean(
        hasPlayedwithOtherPatrons && requiresMorePlayers
      );
      const shouldShowAddPlayersPrivate = Boolean(
        isPrivate &&
          (isLesson || isRequest || hasPlayedWithOthersAndRequiresPlayers)
      );

      if (shouldShowAddPlayersPrivate) {
        steps.push("AddPlayersPrivate");
      } else if (
        hasPlayedWithOthersAndRequiresPlayers ||
        (isRequest && (requestGroupCount ?? 1) > 1)
      ) {
        steps.push("AddPlayers");
      }
      return steps;
    }, [
      groupSize,
      hasPlayedwithOtherPatrons,
      hasPreviousBookings,
      isDurationFacility,
      isLesson,
      isRequest,
      requirePlayerCounts,
      isPrivate,
      availableCapacity,
      isGolf,
      canSelectVenue,
      openSlots,
      requestGroupCount,
    ]);

  return requiredWizardSteps;
};

const useGetRequiredWizardStepsFragmentString = graphql`
  fragment useGetRequiredWizardSteps on ClientProfile
  @argumentDefinitions(forSlotId: { type: "ID", defaultValue: null }) {
    member {
      ... on Member {
        repeatableBookings(
          dateLte: $currentDate
          slotType: $slotType
          forSlotId: $forSlotId
        ) {
          id
          rounds {
            edges {
              node {
                player {
                  id
                }
              }
            }
          }
          ...BookingCard_Booking
        }
      }
      ... on Customer {
        id
        repeatableBookings(
          dateLte: $currentDate
          slotType: $slotType
          forSlotId: $forSlotId
        ) {
          id
          rounds {
            edges {
              node {
                player {
                  id
                }
              }
            }
          }
          ...BookingCard_Booking
        }
      }
      club {
        clients(first: 25, active: true, players: $players) {
          edges {
            node {
              ... on Member {
                id
              }
              ... on Customer {
                id
              }
            }
          }
        }
      }
    }
  }
`;
